import { shallowEqual, useSelector } from 'react-redux'
import Button from '@mui/material/Button'
import Toolbar from '@mui/material/Toolbar'

import { ListGroupSortableField, Role } from 'common/api/v1/types'
import { isEditableGroup, pluralize, useGroupsSelector, usePageParamsFilteredSelector, useUser } from '../../utils'
import Table, { MoveToEntity, TableConfig } from '../common/Table'
import { MissingContent } from '../common/MissingContent'
import Wrapper from '../common/Wrapper'
import { Link } from '../common/Link'
import { EnrichedGroup } from '../../api/nm-types'
import { ReduxSelectedFilters } from '../common/Filters/FilterView/SelectedFilters'
import { mapGroupFilterToUrlParam, mapUrlParamToGroupFilter } from './listGroupFilter'

import { useRoutes, type GlobalState } from '../../store'

export const GroupsList = () => {
  const { groups, total, loading } = usePageParamsFilteredSelector(useGroupsSelector)
  const routes = useRoutes()
  const user = useUser()
  const { devMode } = useSelector(({ settingsReducer }: GlobalState) => settingsReducer, shallowEqual)

  const tableConfig: TableConfig<EnrichedGroup, ListGroupSortableField> = [
    {
      title: 'name',
      getValue: ({ name }) => name,
      sorting: { byParameter: ListGroupSortableField.groupName },
    },
    {
      title: 'users',
      getValue: ({ id, userCount }) =>
        userCount !== undefined && isEditableGroup(id, user) ? pluralize(userCount, 'user') : null,
      sorting: { byParameter: ListGroupSortableField.numberOfUsers, hidden: user.role !== Role.super },
    },
    {
      title: 'appliances',
      getValue: ({ id, applianceCount }) =>
        applianceCount !== undefined && isEditableGroup(id, user) ? pluralize(applianceCount, 'appliance') : null,
      sorting: { byParameter: ListGroupSortableField.numberOfAppliances, hidden: user.role !== Role.super },
    },
    devMode
      ? {
          title: 'interfaces',
          getValue: ({ id, interfaceCount }) =>
            interfaceCount !== undefined && isEditableGroup(id, user) ? pluralize(interfaceCount, 'interface') : null,
          sorting: { byParameter: ListGroupSortableField.numberOfPorts, hidden: user.role !== Role.super },
        }
      : null,
    {
      getValue: ({ id }) => (
        <MoveToEntity url={routes.groupsUpdate({ id })} disabled={!isEditableGroup(id, user)} entityName="group" />
      ),
      props: { align: 'right' },
    },
  ]
  return (
    <Wrapper name="Groups">
      <Toolbar disableGutters>
        <ReduxSelectedFilters
          urlParamCacheKey={'groups'}
          mapUrlParamToFilterFn={mapUrlParamToGroupFilter}
          mapFilterToUrlParamFn={mapGroupFilterToUrlParam}
        />
        <div style={{ marginLeft: 'auto' }}>
          <Link key="manageGroupLists" type="button" to={routes.groupLists()}>
            <Button variant="outlined" color="primary">
              Manage group lists
            </Button>
          </Link>
          {user.role === Role.super && (
            <Link key="addGroup" id="groups-table-create-button" to={routes.groupsNew()}>
              <Button style={{ marginLeft: 8 }} variant="contained" color="primary">
                Add group
              </Button>
            </Link>
          )}
        </div>
      </Toolbar>
      <Table<EnrichedGroup>
        emptyMessageComponent={
          <MissingContent message="No groups available" buttonText="Add group" url={routes.groupsNew()} />
        }
        config={tableConfig}
        data={groups}
        id="groups-table"
        pending={loading}
        pagination={{ total, useUrlSearchParams: true }}
      />
    </Wrapper>
  )
}
